/**
 * Auth Service - React Query Hooks
 * Client-side hooks for authentication operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/query-keys'
import * as authApi from './api'

/**
 * Query: Get current user
 *
 * Fetches current user data from the backend.
 *
 * @param enabled - Optional flag to control if query should run (default: true based on auth state)
 */
export function useCurrentUser(enabled?: boolean) {
  return useQuery({
    queryKey: queryKeys.auth.currentUser(),
    queryFn: authApi.getCurrentUser,
    enabled: enabled !== false, // Allow explicit false to disable
    retry: (failureCount, error: unknown) => {
      // Don't retry on 401 (unauthenticated)
      if ((error as { status?: number })?.status === 401) return false
      return failureCount < 2
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - balance updates via cache invalidation
    // Refetch when user returns to the app
    refetchOnWindowFocus: true,
  })
}

/**
 * Mutation: Send OTP
 */
export function useSendOTP() {
  return useMutation({
    mutationFn: authApi.sendOTP,
  })
}

/**
 * Mutation: Verify OTP and login
 */
export function useVerifyOTP() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authApi.verifyOTP,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all })
    },
  })
}

/**
 * Mutation: Logout
 */
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      refreshToken,
      logoutAll,
    }: {
      refreshToken?: string
      logoutAll?: boolean
    }) => authApi.logout(refreshToken, logoutAll),
    onSuccess: () => {
      queryClient.clear()
    },
  })
}

/**
 * Query: Validate Session
 *
 * Validates the current session using the httpOnly refreshToken cookie.
 * Used during hydration on protected routes to restore auth state when
 * localStorage is out of sync with the cookie.
 *
 * @param enabled - Whether to run the validation (typically true only on protected routes when not authenticated)
 */
export function useValidateSession(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.auth.validate(),
    queryFn: authApi.validateSession,
    enabled,
    retry: false, // Don't retry - if invalid, cookie is cleared
    staleTime: 0, // Always fresh - this is a validation check
  })
}
